import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import useApi from "../../core/customhook/useApi";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";

// ─── Constants ────────────────────────────────────────────────────────────────

const INTERVIEW_ROUNDS = [
  { key: "technical", label: "Technical Round" },
  { key: "hr", label: "HR Round" },
] as const;

const normalizeStatus = (raw: any): string => {
  if (raw == "1" || raw === "pending") return "pending";
  if (raw == "2" || raw === "approved") return "approved";
  if (raw == "3" || raw === "rejected") return "rejected";
  return raw?.toString()?.toLowerCase() || "";
};
// ─── Types ────────────────────────────────────────────────────────────────────

type PositionType = "New Position" | "Replacement Position" | "";

interface ReplacementDetails {
  EmployeeName: string;
  department: string;
  designation: string;
  replacementReason: string;
  band: string;
  ctc: string;
  location: string;
  totalExperience: string;
  qualification: string;
}

interface FormData {
  positionType: PositionType;
  department: string;
  designation: string;
  level: string;
  costcenter: string;
  reportsTo: string;
  withdraw: string | number;
  location: string;
  budgetStatus: string;
  status: string;
  businessNeed: string;
  qualification: string;
  totalExperience: string;
  kras: string[];
  ctc: string;
  PositionName: string;
  manpowerApproved: string;
  overBudget: string;
  expectedJoiningDate: Dayjs | null;
  positionReportingTo: string;
  JD: string | null;
  Replacement: ReplacementDetails;
  interviewRounds: { technical: number; hr: number };
  profile: string;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const INITIAL_FORM_DATA: FormData = {
  positionType: "",
  department: "",
  designation: "",
  level: "",
  costcenter: "",
  withdraw: "",
  reportsTo: "",
  qualification: "",
  totalExperience: "",
  location: "",
  budgetStatus: "",
  businessNeed: "",
  kras: [""],
  ctc: "",
  PositionName: "",
  status: "",
  manpowerApproved: "",
  overBudget: "",
  expectedJoiningDate: null,
  positionReportingTo: "",
  JD: null,
  Replacement: {
    EmployeeName: "",
    department: "",
    designation: "",
    replacementReason: "",
    band: "",
    ctc: "",
    location: "",
    totalExperience: "",
    qualification: "",
  },
  interviewRounds: { technical: 0, hr: 0 },
  profile: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mapApiResponseToFormData = (response: any): FormData => ({
  positionType: response.position_type || "",
  department: response.department?.toString() || "",
  designation: response.designation?.toString() || "",
  level: response.band?.toString() || "",
  costcenter: response.cost_center || "",
  reportsTo: response.position_reporting_to || "",
  qualification: response.qualification || "",
  withdraw: response.withdraw || null,
  totalExperience: response.total_experience || "",
  location: response.location || "",
  budgetStatus: response.budget || "",
  businessNeed: response.business_need || "",
  kras: response.kra?.map((k: any) => k.kra) || [""],
  ctc: response.ctc || "",
  status: normalizeStatus(response.status),
  PositionName: response.position_name || "",
  manpowerApproved: response.manpower_plan || "",
  overBudget: response.justification_overBudgeted || "",
  expectedJoiningDate: response.expected_date_of_joining
    ? dayjs(response.expected_date_of_joining)
    : null,
  positionReportingTo: response.position_reporting_to || "",
  JD: response.jd || null,
  profile: response.profile_name,
  Replacement: {
    EmployeeName: response.replaced_employee || "",
    department: response.department?.toString() || "",
    designation: response.designation?.toString() || "",
    replacementReason: response.replacement_reason || "",
    band: response.band?.toString() || "",
    ctc: response.replacement_ctc || "",
    location: response.location || "",
    totalExperience: response.replacement_total_exp || "",
    qualification: response.replacement_qualification || "",
  },
  interviewRounds: {
    technical: response.technical_round || 0,
    hr: response.hr_round || 0,
  },
});

// ─── Reusable Read-Only Field ─────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "badge bg-warning text-dark" },
    approved: { label: "Approved", className: "badge bg-success" },
    rejected: { label: "Rejected", className: "badge bg-danger" },
  };
  const cfg = config[status] ?? {
    label: status,
    className: "badge bg-secondary",
  };
  return <span className={`${cfg.className} px-3 py-2`}>{cfg.label}</span>;
};

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <div className="col-md-4 mt-2">
    <label className="form-label text-muted">{label}</label>
    <input className="form-control" value={value ?? "—"} readOnly />
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const ViewMprForm = () => {
  const { id } = useParams();

  const isViewMode = Boolean(id);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);

  // API call
  const { callApi: withdrawApi } = useApi("/api/mpr-action-withdraw", {
    autoCall: false,
    method: "POST",
  });

  // ── API Hooks ──────────────────────────────────────────────────────────────

  const { data: mprFormData } = useApi(`/api/mpr/${id}`, {
    autoCall: Boolean(id),
    method: "GET",
  });

  useEffect(() => {
    if (!mprFormData?.data) return;
    setFormData(mapApiResponseToFormData(mprFormData.data));
  }, [mprFormData]);

  // ── Approval / Rejection ───────────────────────────────────────────────────

  const handleSubmission = async (status: "approved" | "rejected") => {
    const isReject = status === "rejected";

    const result = await Swal.fire({
      title: isReject ? "Reject this MPR?" : "Approve this MPR?",
      icon: isReject ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: isReject ? "Yes, reject" : "Yes, approve",
      cancelButtonText: "Cancel",
      confirmButtonColor: isReject ? "#d33" : "#198754",
      ...(isReject && {
        input: "textarea",
        inputPlaceholder: "Enter rejection reason...",
        inputAttributes: { "aria-label": "Rejection reason" },
        preConfirm: (value: string) => {
          if (!value?.trim()) {
            Swal.showValidationMessage("Rejection reason is required");
            return false;
          }
          return value;
        },
      }),
    });

    if (!result.isConfirmed) return;

    const payload = {
      status,
      ...(isReject && { rejectionReason: result.value }),
    };

    console.log("Submitting:", payload);
    // await apiCall(payload);

    Swal.fire(
      "Success!",
      isReject
        ? "Submission has been rejected."
        : "Submission has been approved.",
      "success",
    );
  };

  // handle withdraw

  const handleWithdraw = async (mpr_id: string | any) => {
    // Show confirmation modal
    const result = await Swal.fire({
      title: "Withdraw this MPR?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, withdraw",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    // If user cancels
    if (!result.isConfirmed) return;

    try {
      // Prepare payload
      const payload = { mpr_id };
      console.log("Withdrawing MPR:", payload);

      // Call the API using your hook
      const response = await withdrawApi(payload);

      if (response.success && response.statusCode === 200) {
        navigate("/view-recruitment");
      }

      // Show success message
      Swal.fire(
        "Withdrawn!",
        response?.data?.message || "The MPR has been withdrawn successfully.",
        "success",
      );
    } catch (error: any) {
      // Show error message
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to withdraw MPR.",
        "error",
      );
      console.error("Withdraw API error:", error);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Manpower Requisition</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Recruitment</li>
                <li className="breadcrumb-item active">View Requisition</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <CollapseHeader />
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {/* ── MPR ID & Status Row (View Mode Only) ── */}
            {isViewMode && (
              <section className="mb-4 pb-3 border-bottom">
                <div className="row g-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">MPR ID</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={id || ""}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Status</label>
                    <div className="mt-1">
                      <StatusBadge status={formData.status} />
                    </div>
                  </div>
                </div>
              </section>
            )}
            {/* ── Position Type ── */}
            <section className="mb-4">
              <h5 className="mb-3">Position Type</h5>
              <div className="d-flex gap-4">
                {(
                  ["New Position", "Replacement Position"] as PositionType[]
                ).map((type) => (
                  <div key={type} className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      className="form-check-input"
                      checked={formData.positionType === type}
                      readOnly
                      disabled
                    />
                    <label className="form-label mb-0 text-muted">{type}</label>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Position Justification ── */}
            <section className="mb-4">
              <h5 className="mb-3">Position Justification</h5>

              <div className="mb-3">
                <label className="form-label text-muted">Business Need</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.businessNeed}
                  readOnly
                />
              </div>

              <div className="mb-3">
                <label className="form-label text-muted">
                  Top 4 Expected KRAs
                </label>
                {formData.kras.map((kra, index) => (
                  <input
                    key={index}
                    type="text"
                    className="form-control mb-2"
                    value={kra}
                    readOnly
                  />
                ))}
              </div>
            </section>

            {/* ── Replacement Details ── */}
            {formData.positionType === "Replacement Position" && (
              <section className="mb-4">
                <h5 className="mb-3">Details for Replacement Position</h5>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Employee Being Replaced
                    </label>
                    <input
                      className="form-control"
                      value={formData.Replacement.EmployeeName}
                      readOnly
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  {[
                    {
                      label: "Department",
                      value: formData.Replacement.department,
                    },
                    {
                      label: "Designation",
                      value: formData.Replacement.designation,
                    },
                    // { label: "Band", value: formData.Replacement.band },
                    { label: "Location", value: formData.Replacement.location },
                    { label: "CTC", value: formData.Replacement.ctc },
                  ].map(({ label, value }) => (
                    <div className="col" key={label}>
                      <label className="form-label text-muted">{label}</label>
                      <input
                        className="form-control"
                        value={value || "—"}
                        readOnly
                      />
                    </div>
                  ))}
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-5">
                    <label className="form-label text-muted">
                      Total Experience
                    </label>
                    <input
                      className="form-control"
                      value={formData.Replacement.totalExperience}
                      readOnly
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label text-muted">
                      Qualification
                    </label>
                    <input
                      className="form-control"
                      value={formData.Replacement.qualification}
                      readOnly
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label text-muted">
                    Reason for Replacement
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.Replacement.replacementReason}
                    readOnly
                  />
                </div>
              </section>
            )}

            {/* ── Position Details ── */}
            <section className="mb-4">
              <h5 className="mb-3">Details for New Position</h5>

              <div className="row g-3">
                <ReadOnlyField
                  label="Position Name"
                  value={formData.PositionName}
                />
                <ReadOnlyField label="Profile Name" value={formData.profile} />
                <ReadOnlyField label="Department" value={formData.department} />
                <ReadOnlyField
                  label="Designation"
                  value={formData.designation}
                />
                {/* <ReadOnlyField label="Band" value={formData.level} /> */}
                {/* <ReadOnlyField
                  label="Cost Center"
                  value={formData.costcenter}
                /> */}
                <ReadOnlyField
                  label="Position Reporting To"
                  value={formData.positionReportingTo}
                />
                <ReadOnlyField label="Location" value={formData.location} />
                <ReadOnlyField
                  label="Manpower Plan Approved"
                  value={formData.manpowerApproved}
                />
                <ReadOnlyField
                  label="Total Experience"
                  value={formData.totalExperience}
                />
                <ReadOnlyField
                  label="Qualification"
                  value={formData.qualification}
                />
                <ReadOnlyField label="CTC" value={formData.ctc} />
                <ReadOnlyField
                  label="Budget Status"
                  value={formData.budgetStatus}
                />

                {formData.overBudget && (
                  <div className="col-md-4 mt-2">
                    <label className="form-label text-muted">
                      Justification for Over Budget
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={formData.overBudget}
                      readOnly
                    />
                  </div>
                )}

                <div className="col-md-4 mt-2">
                  <label className="form-label text-muted">
                    Expected Date of Joining
                  </label>
                  <DatePicker
                    className="form-control"
                    format="DD-MMMM-YYYY"
                    value={formData.expectedJoiningDate}
                    inputReadOnly
                    open={false}
                    allowClear={false}
                  />
                </div>

                <div className="col-md-4 mt-2">
                  <label className="form-label text-muted">
                    Job Description
                  </label>
                  <div>
                    {formData?.JD ? (
                      <a
                        href={formData.JD}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary"
                      >
                        View JD
                      </a>
                    ) : (
                      <span className="text-muted">No JD uploaded</span>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* ── Interview Rounds ── */}
            <section className="row mb-4">
              {INTERVIEW_ROUNDS.map(({ key, label }) => (
                <div className="col-md-6" key={key}>
                  <label className="form-label fw-medium text-muted">
                    {label}
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="number"
                      className="form-control text-center"
                      style={{ width: 80 }}
                      value={formData.interviewRounds[key]}
                      readOnly
                    />
                  </div>
                </div>
              ))}
            </section>

            {/* ── Actions ── */}
            <div className="d-flex justify-content-end gap-2">
              {formData.status === "pending" && formData?.withdraw !== 1 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleWithdraw(id)}
                >
                  Withdraw
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <footer className="footer d-flex align-items-center justify-content-center border-top bg-white p-3">
        <p className="mb-0"> © {new Date().getFullYear()} WeConnect. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default ViewMprForm;
